import { Link } from "react-router-dom";
import { Phone, MessageSquare, ArrowLeft, ShieldCheck, Wrench } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SHINGLE_LINES, PHONE, PHONE_TEL } from "@/lib/constants";

export default function Shingles() {
  return (
    <>
      <Header />

      <section className="border-b border-slate-900 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-16">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-red-600 mb-6 hover:text-red-500"
          >
            <ArrowLeft size={14} /> Back home
          </Link>
          <h1 className="font-display font-black uppercase text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            Choose your <span className="text-red-600">shingles</span>.
          </h1>
          <p className="text-slate-300 text-lg md:text-xl max-w-2xl mt-6 leading-relaxed">
            After your inspection is approved, you'll pick from these premium GAF Timberline shingle
            lines. Every option carries a limited lifetime warranty.
          </p>
        </div>
      </section>

      <section data-testid="shingle-gallery" className="py-20 md:py-28 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-20">
          {SHINGLE_LINES.map((s, i) => (
            <article
              data-testid={`shingle-line-${i}`}
              key={s.name}
              className="grid lg:grid-cols-12 gap-10 items-start"
            >
              <div className="lg:col-span-7 bg-white border border-slate-800 overflow-hidden">
                <img src={s.image} alt={s.name} className="w-full h-auto block" />
              </div>
              <div className="lg:col-span-5 lg:sticky lg:top-28">
                {s.tier && (
                  <div
                    data-testid={`tier-${i}`}
                    className="inline-block font-impact uppercase text-white text-6xl sm:text-7xl tracking-tight mb-4 pb-2 border-b-4 border-red-600"
                  >
                    {s.tier}
                  </div>
                )}
                <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-3">
                  {s.tag}
                </div>
                <h2 className="font-display font-black uppercase text-3xl sm:text-4xl text-white leading-none tracking-tight">
                  {s.name}
                </h2>

                {s.upgrade && (
                  <div
                    data-testid={`pricing-${i}`}
                    className={`mt-5 inline-flex items-baseline gap-3 border-2 px-5 py-3 ${
                      s.upgrade.free
                        ? "border-red-600 bg-red-600/10"
                        : "border-slate-700 bg-slate-900/60"
                    }`}
                  >
                    <span className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">
                      {s.upgrade.label}
                    </span>
                    <span className="font-display font-black text-white text-xl uppercase tracking-tight">
                      {s.upgrade.price}
                    </span>
                  </div>
                )}

                <p className="text-slate-400 mt-5 leading-relaxed text-lg">{s.blurb}</p>

                <div className="mt-8">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">
                    Popular colors
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.colors.map((c) => (
                      <span
                        key={c}
                        className="text-xs uppercase tracking-widest font-bold text-slate-200 border border-slate-700 px-3 py-2"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>

                {s.warranty && (
                  <div className="mt-8 border-t border-slate-800 pt-6">
                    <div className="text-xs uppercase tracking-widest text-red-600 mb-4">
                      Warranty coverage
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-3 bg-slate-900/60 border border-slate-800 p-4">
                        <Wrench size={18} className="text-red-600 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-display font-bold text-white text-sm uppercase tracking-wide">
                            {s.warranty.labor}
                          </div>
                          <div className="text-slate-400 text-xs mt-1">Workmanship covered</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 bg-slate-900/60 border border-slate-800 p-4">
                        <ShieldCheck size={18} className="text-red-600 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-display font-bold text-white text-sm uppercase tracking-wide">
                            {s.warranty.material}
                          </div>
                          <div className="text-slate-400 text-xs mt-1">Backed by GAF</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20 border-t border-slate-900 bg-slate-900/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">
            Ready to start
          </div>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl text-white tracking-tight">
            Book your free inspection.
          </h2>
          <p className="text-slate-400 mt-5 max-w-xl mx-auto">
            JD verifies claim eligibility for wind, hail, and tree damage across 16 Georgia counties.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
            <a
              data-testid="shingles-call"
              href={`tel:${PHONE_TEL}`}
              className="bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 px-8 py-4 inline-flex items-center justify-center gap-2"
            >
              <Phone size={16} /> Call JD · {PHONE}
            </a>
            <a
              data-testid="shingles-text"
              href={`sms:${PHONE_TEL}`}
              className="bg-transparent border-2 border-slate-700 text-white font-bold uppercase tracking-wide hover:border-red-600 hover:text-red-600 px-8 py-4 inline-flex items-center justify-center gap-2"
            >
              <MessageSquare size={16} /> Text JD
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
